/** Shared producer/completion protocol for native queued bills and task validation; outbox identity remains the authority boundary. */
import { AIUserContext } from './ai.auth.model';
import { ScanLineItemRequest, TaskSubmissionValidationRequest } from './ai.workflow.model';

//////////////////////////////////////////////////////////////////////////////
// DURABLE AI JOBS
//
// Work the tenant API hands to the AI service through the message reliability
// outbox and RabbitMQ instead of a direct HTTP call. The envelope travels out
// on the invoke topic, the completion travels back on the completed topic, and
// the two are matched by the outbox record id.
//////////////////////////////////////////////////////////////////////////////

/** Kinds of work that can travel through the durable AI jobs queue. */
export type AiJobKind = 'task-validation' | 'bill-scan';

/** Message reliability process names, one per job kind. */
export enum AiJobsReliabilityProcessName {
    TaskValidation = 'ai-job-task-validation',
    BillScan = 'ai-job-bill-scan',
}

/** Per-kind job input. */
export type AiJobPayload = TaskSubmissionValidationRequest | ScanLineItemRequest | Record<string, unknown>;

/**
 * The outbox payload for one queued job.
 *
 * On the wire the outbox dispatcher wraps it as `AiJobDeliveryMessage` because the AI job
 * processes defer their delivery status to the completion worker.
 */
export interface AiJobEnvelope {
    /** The outbox record id; also the idempotency key on both sides of the queue. */
    outboxId: string;
    kind: AiJobKind;
    /** Tenant slug; also the RabbitMQ `tenant` header. */
    tenant: string;
    /** Same value as `currentTenantUrl` in the HTTP request context. */
    tenantUrl: string;
    actorUserId: string;
    /** The actor as the AI user context the HTTP path carries under `currentUser`, so the AI service can attribute and display the run the same way. */
    currentUser?: AIUserContext;
    /** Same value as `blobRoot` in the HTTP request context: the tenant blob store root used to resolve file URLs. */
    blobRoot?: string;
    /** ISO timestamp. */
    enqueuedAt: string;
    /** ISO timestamp: `enqueuedAt` plus the pickup window. The consumer measures the window from the delivery's `publishedAt` and falls back to this only when that is absent. */
    pickupDeadlineAt: string;
    /** How long the job may run once picked up. */
    runAllowanceMs: number;
    /** Per-delivery pickup window, selected by workload registration. */
    queueWaitMs?: number;
    /** Delivery retry count, independent of native execution attempts. */
    queueAttempt?: number;
    queueRetryLimit?: number;
    /** Fixed deadline for the current pickup round; retries cannot renew it. */
    queueBudgetDeadlineAt?: string;
    /** Optional submission-based cap that remains unchanged across every retry. */
    endToEndDeadlineAt?: string;
    payload: AiJobPayload;
    /** The AIProcessInbox record id created at enqueue, when the kind has one. */
    processId?: string;
}

/**
 * Broker-only form of an AI job. The pass is minted after the outbox row is claimed and is added
 * only to the in-memory RabbitMQ payload; it must never be written back to the outbox document.
 */
export interface AiJobExecutionEnvelope extends AiJobEnvelope {
    pass: string;
}

/** Claims carried by the broker-only, session-independent job pass. */
export interface AiJobAuthorityClaims {
    _id: string;
    actingAsAiJob: true;
    aiJob: {
        outboxId: string;
        kind: AiJobKind;
        processId: string;
    };
    iss: string;
    aud: string;
    iat?: number;
    exp?: number;
}

/**
 * The outbox delivery envelope the message body carries when `deferDeliveryStatus` is on.
 *
 * `publishedAt` is stamped by the dispatcher on every publish, including the re-publish of a job
 * released for its retry, so the consumer measures the pickup window from the publish and never
 * from the original enqueue.
 */
export interface AiJobDeliveryMessage {
    outboxId: string;
    process: string;
    payload: AiJobExecutionEnvelope;
    /** ISO timestamp of this publish. */
    publishedAt?: string;
}

/**
 * Outcome the AI service reports for a queued job on the completed topic; the API completion
 * worker settles the outbox record from it. `failed` may still be retried, see `retryable`.
 */
export type AiJobCompletionStatus = 'completed' | 'failed';

/**
 * Why a queued job failed, set by the AI service and read by the API completion worker to choose
 * between a retry and a final failure. Each deadline has its own reason so they are never confused:
 * - `expired`: nobody picked the delivery up within its pickup window.
 * - `interrupted`: a server shutdown cut the run off and dead-lettered the job back to the API.
 * - `allowance-exceeded`: the run outlasted `runAllowanceMs` and was cancelled.
 * - `end-to-end-exceeded`: the optional submission-wide deadline passed.
 * - `run-error`: the workflow itself failed.
 * - `invalid-envelope`: the delivery failed validation and was never run.
 * - `duplicate-run`: another execution already owns or finished this job.
 */
export type AiJobFailureReason = 'expired' | 'interrupted' | 'run-error' | 'allowance-exceeded' | 'invalid-envelope' | 'duplicate-run' | 'end-to-end-exceeded';

/** What the AI service publishes back once a queued job has finished or been refused. */
export interface AiJobCompletion {
    outboxId: string;
    kind: AiJobKind;
    tenant: string;
    status: AiJobCompletionStatus;
    /** Meaningful only when failed: whether the job may be re-published for another attempt. */
    retryable?: boolean;
    reason?: AiJobFailureReason;
    /** The workflow result when completed. */
    result?: unknown;
    error?: { message: string; code?: string };
    runId?: string;
    /** ISO timestamp. */
    completedAt: string;
}
